import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Heart, Moon, Sun, Settings, Menu, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface HeaderProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  className?: string;
  placeholder?: string;
}

export function Header({ searchQuery = '', onSearchChange = () => {}, className, placeholder = "Search tools... (Press / to focus)" }: HeaderProps) {
  const [isDark, setIsDark] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    setIsDark(isDarkMode);
  }, []);

  const toggleTheme = () => {
    document.documentElement.classList.toggle('dark');
    setIsDark(!isDark);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onSearchChange('');
      // Close mobile search on escape
      if (mobileSearchOpen) {
        setMobileSearchOpen(false);
      }
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Navigate to appropriate page based on current location and search type
    const currentPath = location.pathname;
    if (currentPath.startsWith('/resources')) {
      navigate(`/resources?q=${encodeURIComponent(searchQuery)}`);
    } else if (currentPath.startsWith('/job-boards')) {
      navigate(`/job-boards?q=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate(`/tools?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Focus search on '/' or 'Cmd+K'
      if (e.key === '/' || (e.metaKey && e.key === 'k')) {
        e.preventDefault();
        const searchInput = document.querySelector('#search-input') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <header className={cn(
      "sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
      className
    )}>
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2"
            aria-label="DesignStack - Home"
          >
            <div className="text-xl font-heading font-semibold text-primary flex items-center space-x-2">
              <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold">
                D
              </div>
              <span className="hidden xs:block">DesignStack</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <Link 
              to="/tools"
              className={cn(
                "text-sm font-medium transition-colors duration-base",
                location.pathname === '/tools' || location.pathname.startsWith('/tool/')
                  ? "text-primary" 
                  : "text-text-secondary hover:text-primary"
              )}
            >
              Tools
            </Link>
            <Link 
              to="/resources"
              className={cn(
                "text-sm font-medium transition-colors duration-base",
                location.pathname === '/resources' || location.pathname.startsWith('/resources/')
                  ? "text-primary" 
                  : "text-text-secondary hover:text-primary"
              )}
            >
              Resources
            </Link>
            <Link 
              to="/job-boards"
              className={cn(
                "text-sm font-medium transition-colors duration-base",
                location.pathname === '/job-boards'
                  ? "text-primary" 
                  : "text-text-secondary hover:text-primary"
              )}
            >
              Job Boards
            </Link>
            <Link 
              to="/favorites"
              className={cn(
                "text-sm font-medium transition-colors duration-base",
                location.pathname === '/favorites'
                  ? "text-primary" 
                  : "text-text-secondary hover:text-primary"
              )}
            >
              Favorites
            </Link>
          </nav>

          {/* Search - Desktop/Tablet */}
          {!location.pathname.startsWith('/about') && location.pathname !== '/' && (
            <div className="hidden md:flex flex-1 max-w-lg mx-6">
              <form onSubmit={handleSearchSubmit} className="relative w-full">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
                <Input
                  id="search-input"
                  type="search"
                  placeholder={placeholder}
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="pl-10 pr-12 h-10 bg-surface border-border focus:ring-accent focus:border-accent w-full"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-tertiary hidden lg:block">
                  ⌘K
                </div>
              </form>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-1">
            {/* Search Button - Mobile only */}
            {!location.pathname.startsWith('/about') && location.pathname !== '/' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMobileSearchOpen(!mobileSearchOpen);
                  if (!mobileSearchOpen) {
                    // Focus the input after animation completes
                    setTimeout(() => {
                      const searchInput = document.querySelector('#mobile-search-input') as HTMLInputElement;
                      if (searchInput) {
                        searchInput.focus();
                      }
                    }, 150);
                  }
                }}
                className="text-text-secondary hover:text-primary md:hidden"
                aria-label="Toggle search input"
              >
                {mobileSearchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="text-text-secondary hover:text-primary hidden sm:flex"
            >
              <Link to="/settings" aria-label="Open settings">
                <Settings className="h-4 w-4" />
              </Link>
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="text-text-secondary hover:text-primary"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-text-secondary hover:text-primary lg:hidden"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col space-y-6 mt-6">
                  <nav className="flex flex-col space-y-4">
                    <Link 
                      to="/tools"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "text-base font-medium transition-colors duration-base px-4 py-2 rounded-lg",
                        location.pathname === '/tools' || location.pathname.startsWith('/tool/')
                          ? "text-primary bg-primary/10" 
                          : "text-text-secondary hover:text-primary hover:bg-accent/50"
                      )}
                    >
                      Tools
                    </Link>
                    <Link 
                      to="/resources"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "text-base font-medium transition-colors duration-base px-4 py-2 rounded-lg",
                        location.pathname === '/resources' || location.pathname.startsWith('/resources/')
                          ? "text-primary bg-primary/10" 
                          : "text-text-secondary hover:text-primary hover:bg-accent/50"
                      )}
                    >
                      Resources
                    </Link>
                    <Link 
                      to="/job-boards"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "text-base font-medium transition-colors duration-base px-4 py-2 rounded-lg",
                        location.pathname === '/job-boards'
                          ? "text-primary bg-primary/10" 
                          : "text-text-secondary hover:text-primary hover:bg-accent/50"
                      )}
                    >
                      Job Boards
                    </Link>
                    <Link 
                      to="/favorites"
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "text-base font-medium transition-colors duration-base px-4 py-2 rounded-lg",
                        location.pathname === '/favorites'
                          ? "text-primary bg-primary/10" 
                          : "text-text-secondary hover:text-primary hover:bg-accent/50"
                      )}
                    >
                      Favorites
                    </Link>
                    <Link 
                      to="/settings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-base font-medium text-text-secondary hover:text-primary hover:bg-accent/50 px-4 py-2 rounded-lg transition-colors duration-base"
                    >
                      Settings
                    </Link>
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {!location.pathname.startsWith('/about') && location.pathname !== '/' && mobileSearchOpen && (
          <div className="md:hidden pb-4 animate-fade-in">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
              <Input
                id="mobile-search-input"
                type="search"
                placeholder={placeholder.replace(" (Press / to focus)", "")}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10 pr-4 h-10 bg-surface border-border focus:ring-accent focus:border-accent"
              />
            </form>
          </div>
        )}
      </div>
    </header>
  );
}